import React, { useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { useNavigate, useParams } from "react-router";
import { enumWorkflowState, enumObjectPersistenceState, enumPersistableObjectType, enumImageLinkType, enumBeaconType, enumPersistableObjectTypeConvert } from '../../../dataObjects/enums';
import { IStoreState } from '../../../uiMiddleware-redux/store/IStoreState';
import { MessagesStringAssets, NotificationStringAssets, PageAndViewTitleStringAssets } from '../../../assets/stringAssets';
import { typeUniqueId } from '../../../dataObjects/types';
import { IImageLink, ImageLink } from '../../../dataObjects/models/digitalMedia/ImageLink';
import { RandomId } from '../../../dataObjects/utilities/RandomId';
import { IUserPersistenceData, UserPersistenceData } from '../../../dataObjects/models/persistence/UserPersistenceData';
import { IUser } from '../../../dataObjects/models/users/User';
import { IUserSettings } from '../../../dataObjects/models/users/UserSettings';
import { getSingleObjectById } from '../../../firebaseServices/dataServices/dataServiceActions/genericActions';
import { ISaveImageLinkRequest } from '../../../dataObjects/models/serviceRequests/imageLink/SaveImageLinkRequest';
import { ISaveImageLinkWorkflowState } from '../../../uiMiddleware-redux/store/SaveImageWorkflowState';
import GenericPageContainer from '../GenericPageContainer/GenericPageContainer';
import { AlertInfo, IAlertInfo } from '../../../dataObjects/models/alerts/AlertInfo';
import { enumAlertType } from '../../enums';
import { Beacon } from '../../../dataObjects/models/alerts/Beacon';
import { ICurrentUserContextData, useCurrentUserContext } from '../../providersAndContexts/currentUser';
import { useAppDispatch, useAppSelector } from '../../../uiMiddleware-redux/store/configureStore';
import { imageLinkSaveStatusClear, imageLinkSaveWithFileUploadRequest, imageLinkSaveWithoutFileUploadRequest, imageLinkSaveWorkflowStateChange } from '../../../uiMiddleware-redux/slices/imageLink/imageLinkSaveStatusSlice';
import { alertInfoChange } from '../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';
import { beaconChange } from '../../../uiMiddleware-redux/slices/beacon/beaconSlice';
import ProfileImageLinkForm from '../../forms/ProfileImageLinkForm/ProfileImageLinkForm';
import { getChannelObjectUsingId } from '../../../firebaseServices/dataServices/dataServiceActions/channelActions';
import { IChannel } from '../../../dataObjects/models/channels/Channel';
import { channelSaveRequest } from '../../../uiMiddleware-redux/slices/channel/channelSaveStatusSlice';
import { IPersistable } from '../../../dataObjects/models/persistence/Persistable';
import { ITopic, ITopicAsJson } from '../../../dataObjects/models/topics/Topic';
import { getTopicObjectUsingId } from '../../../firebaseServices/dataServices/dataServiceActions/topicActions';
import { topicSaveRequest } from '../../../uiMiddleware-redux/slices/topic/topicSaveStatusSlice';
import { getTopicItemObjectUsingId } from '../../../firebaseServices/dataServices/dataServiceActions/topicItemActions';
import { topicItemSaveRequest } from '../../../uiMiddleware-redux/slices/topicItem/topicItemSaveStatusSlice';
import { ITopicItem } from '../../../dataObjects/models/topics/TopicItem';
import { ICategory } from '../../../dataObjects/models/categories/Category';
import { categorySaveRequest } from '../../../uiMiddleware-redux/slices/category/categorySaveStatusSlice';
import { getCategoryObjectUsingId } from '../../../firebaseServices/dataServices/dataServiceActions/categoryActions';
import { userSaveRequest } from '../../../uiMiddleware-redux/slices/user/userSaveStatusSlice';

/**
 * @interface IManageProfileImageLinkPage Input properties for the ManageProfileImageLinkPage
 */
export interface IManageProfileImageLinkPageProps extends PropsWithChildren<unknown> {
  id?: typeUniqueId,  // if the Id is given, it means that the ProfileImageLink already exists
  parentId?: typeUniqueId,  // if the parentId is given (parentId of the ProfileImageLink parent object), it means that a new ProfileImageLink is to be created
  parentType?: enumPersistableObjectType, // the parentType must be provided only if a new ProfileImageLink is to be created
  descriptionForProfileImageLink?: string // the descriptionForProfileImageLink must be provided only if a new ProfileImageLink is to be created
}

/**
 * @function ManageProfileImageLinkPage A React Function Component for manageing (creating or editing) a ProfileImageLink.
 * @param {IManageProfileImageLinkPageProps} props Input properties
 */
const ManageProfileImageLinkPage: React.FC<IManageProfileImageLinkPageProps> = (props: IManageProfileImageLinkPageProps) => {

  ManageProfileImageLinkPage.displayName = "Manage Profile Image Link Page";

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const params = useParams();

  const { descriptionForProfileImageLink } = props;

  // will be set to the 'id' provided in the URL parms. If an id was provided, it means that we will be editing an existing object.
  const [profileImageLinkId, setProfileImageLinkId] = useState<typeUniqueId | undefined>(params.id);

  // define an effect that will set the profileImageLinkId anytime the params.id changes
  useEffect(() => {
    setProfileImageLinkId(params.id);
  }, [params.id]);

  // define an effect based on a change to the profileImageLinkId. Having a defined profileImageLinkId means that we will 
  // be editing an existing ImageLink
  useEffect(() => {
    if (profileImageLinkId !== undefined) {
      // try to find an ImageLink object with the Id given in the URL
      getSingleObjectById(profileImageLinkId, enumPersistableObjectType.ImageLink, onImageLinkFetched);
    }
  }, [profileImageLinkId]);

  // will be set to the 'parentId' provided in the URL parms. If a parentId was provided, it means that we will be creating a new object.
  const [profileImageLinkParentId, setProfileImageLinkParentId] = useState<typeUniqueId | undefined>(params.parentId);

  // define an effect that will set the profileImageLinkParentId anytime the params.parentId changes
  useEffect(() => {
    setProfileImageLinkParentId(params.parentId);
  }, [params.parentId]);

  // will be set to the profileImageLink to be managed, either a new profileImageLink or an existing one
  const [profileImageLink, setProfileImageLink] = useState<IImageLink | undefined>(undefined);

  // whether we are going to be managing a new profileImageLink
  const [isNewObject, setIsNewObject] = useState<boolean>(true);

  const resetSaveImageLinkStatus: () => void = useCallback(() => {
    dispatch(imageLinkSaveStatusClear());
  }, [dispatch])

  const resetAlertInfo: () => void = useCallback(() => {
    dispatch(alertInfoChange(null));
  }, [dispatch])

  // perform any initialization required for this page when it comes into existence (mounts) and goes out of existence (unmounts)
  useEffect(() => {
    // clear areas in Redux state to start with a clean slate
    resetSaveImageLinkStatus();
    resetAlertInfo();

    return () => {
      // upon 'unmounting', clear areas of Redux state
      resetSaveImageLinkStatus();
      resetAlertInfo();
    }
  }, [resetAlertInfo, resetSaveImageLinkStatus]);

  // use a custom hook to get the Current User information from the CurrentUserProvider
  const { currentUser }: ICurrentUserContextData = useCurrentUserContext();

  // workflow state related to saving an ImageLink
  const [saveImageWorkflowState, setSaveImageWorkflowState] = useState<ISaveImageLinkWorkflowState | null | undefined>(undefined);

  // indicates whether a new profile image has been successfully saved
  const [successfullySavedProfileImage, setSuccessfullySavedProfileImage] = useState<boolean>(false);

  // indicates whether the page has been successfully completed (for saving new object or updating existing)
  const [successfullyCompletedPage, setSuccessfullyCompletedPage] = useState<boolean>(false);

  const updateProfileImageReferenceForParent: () => void = useCallback(
    () => {
      if (profileImageLink && profileImageLink.parentId) {

        // get the parent object for the profile image based on its type, so we can update the reference to the profile image within the parent object...
        switch (params.parentType) {
          case enumPersistableObjectType.Category:
            getCategoryObjectUsingId(profileImageLink.parentId).then((category: ICategory | undefined) => {
              // if we retrieved a valid category object...
              if (category !== undefined) {
                // set its profileImageId
                category.profileImageId = profileImageLink.id;

                // dispatch a call to save the channel (this will be a "fire and forget" operation that assumes the save operation will succeed)
                dispatch(categorySaveRequest(category));
              }
            })

            // set flag to indicate that the page has been successfully completed, which will force a re-render to allow for finalizing work for the page
            setSuccessfullyCompletedPage(true);
            break;

          case enumPersistableObjectType.Channel:
            getChannelObjectUsingId(profileImageLink.parentId).then((channel: IChannel | undefined) => {
              // if we retrieved a valid channel object...
              if (channel !== undefined) {
                // set its profileImageId
                channel.profileImageId = profileImageLink.id;

                // dispatch a call to save the channel (this will be a "fire and forget" operation that assumes the save operation will succeed)
                dispatch(channelSaveRequest({ channel: channel, parentCategoryId: channel.categoryId ?? '' }));
              }
            })

            // set flag to indicate that the page has been successfully completed, which will force a re-render to allow for finalizing work for the page
            setSuccessfullyCompletedPage(true);
            break;

          case enumPersistableObjectType.Topic:
            getTopicObjectUsingId(profileImageLink.parentId).then((topic: ITopic | undefined) => {
              // if we retrieved a valid topic object...
              if (topic !== undefined) {
                // set its profileImageId
                topic.profileImageId = profileImageLink.id;

                // dispatch a call to save the topic (this will be a "fire and forget" operation that assumes the save operation will succeed)
                dispatch(topicSaveRequest(topic));
              }
            })

            // set flag to indicate that the page has been successfully completed, which will force a re-render to allow for finalizing work for the page
            setSuccessfullyCompletedPage(true);
            break;

          case enumPersistableObjectType.TopicItem:
            getTopicItemObjectUsingId(profileImageLink.parentId).then((topicItem: ITopicItem | undefined) => {
              // if we retrieved a valid topicItem object...
              if (topicItem !== undefined) {
                // set its profileImageId
                topicItem.profileImageId = profileImageLink.id;

                // dispatch a call to save the topic (this will be a "fire and forget" operation that assumes the save operation will succeed)
                dispatch(topicItemSaveRequest(topicItem));
              }
            })

            // set flag to indicate that the page has been successfully completed, which will force a re-render to allow for finalizing work for the page
            setSuccessfullyCompletedPage(true);
            break;

          case enumPersistableObjectType.UserSettings:
            // assign the profileImageId to the currentUser.userSettings object, and then save it.
            if (currentUser) {
              currentUser.userSettings.profileImageId = profileImageLink.id;

              // dispatch a call to save the userSettings (this will be a "fire and forget" operation that assumes the save operation will succeed)
              dispatch(userSaveRequest(currentUser));

              // // indicate that we do not want to navigate back a page upon a save operation
              // setNavigatePageBackOnSave(false);

              // set flag to indicate that the page has been successfully completed, which will force a re-render to allow for finalizing work for the page
              setSuccessfullyCompletedPage(true);
            }
            break;

          default:
            // do nothing for other types 
            break;
        }
      }

    }, [dispatch, params.parentType, profileImageLink]
  );

  // define an effect that will process whenever it's a new object and the successfullySavedProfileImage state changes
  useEffect(() => {
    // if saving a new profile image...
    if (isNewObject) {

      // if the profile image has just been successfully saved
      if (successfullySavedProfileImage) {

        // dispatch to clear the imageLink save status store state variable
        dispatch(imageLinkSaveWorkflowStateChange(null));

        // notify user of the successful operation
        // the success message for ImageLink will merely be the defined constant
        const successMessage: string = MessagesStringAssets.imageLink_SaveSuccess;
        dispatch(beaconChange(new Beacon(undefined, enumBeaconType.Success, NotificationStringAssets.heading_SaveImageLink, successMessage)));

        // call function to update the profile image link for its parent object
        updateProfileImageReferenceForParent();
      }
    }
  }, [isNewObject, successfullySavedProfileImage, dispatch, navigate, updateProfileImageReferenceForParent]);


  // define an effect that will process whenever the successfullyCompletedPage state changes
  useEffect(() => {
    if (successfullyCompletedPage) {
      // return to previous page
      navigate(-1);
    }
  }, [successfullyCompletedPage, dispatch, navigate]);

  // get the Redux workflow state for saving an ImageLink
  const currentSaveImageLinkWorkflowState: ISaveImageLinkWorkflowState | null | undefined = useAppSelector((state: IStoreState) => state.saveImageLinkStatus);

  // whenever the workflow state changes, set the object's value into the local state
  useEffect(() => {
    setSaveImageWorkflowState(currentSaveImageLinkWorkflowState);
  }, [currentSaveImageLinkWorkflowState]);

  /**
   * @function prepareNewImageLink Prepares/creates a new profileImageLink that will be used if user is requesting to create/add a new profileImageLink.
   * @param parentChannel 
   */
  const prepareNewProfileImageLink: (parentId: typeUniqueId, user: IUser, userSettings: IUserSettings) => IImageLink = useCallback(
    (parentId: typeUniqueId, user: IUser, userSettings: IUserSettings) => {
      let newProfileImageLink: IImageLink;

      const newImageLinkId: typeUniqueId = RandomId.newId();
      const userName: string = `${userSettings.firstName} ${userSettings.lastName}`;
      const userPersistenceData: IUserPersistenceData = new UserPersistenceData(user.id, userName);
      const resolvedParentType: enumPersistableObjectType = params.parentType ? enumPersistableObjectTypeConvert.fromString(params.parentType) : enumPersistableObjectType.Channel;
      const resolvedDescription: string = descriptionForProfileImageLink ?? 'Profile Image';
      newProfileImageLink = new ImageLink(user.id, newImageLinkId, resolvedParentType, parentId, enumObjectPersistenceState.New,
        enumImageLinkType.GoogleCloudStorage, '', '', '', resolvedDescription, userPersistenceData);

      return newProfileImageLink;
    }, [descriptionForProfileImageLink, params.parentType]
  );

  // define an effect based on a change to the profileImageLinkParentId & currentUser. Having a defined profileImageLinkParentId means that we will
  // be creating a new ImageLink
  useEffect(() => {
    if (profileImageLinkParentId !== undefined && currentUser) {
      setProfileImageLink(prepareNewProfileImageLink(profileImageLinkParentId, currentUser, currentUser.userSettings));
    }
  }, [profileImageLinkParentId, currentUser, prepareNewProfileImageLink]);


  function onImageLinkFetched(profileImageLink: IImageLink | undefined): void {
    if (profileImageLink !== undefined) {
      // set the returned ImageLink to be the one we are editing
      setProfileImageLink(profileImageLink);

      // set local 'isNewObject' state to 'false' to indicate that we are editing an existing object (default was set to 'true')
      setIsNewObject(false);
    }
  }

  /**
   * @method handleFormSubmit Handles a submit request from the form in this page
   * @param {ISaveImageLinkRequest} saveImageLinkRequest A Submit event
   */
  function handleFormSubmit(saveImageLinkRequest: ISaveImageLinkRequest): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        // dispatch an appropriate action to request saving the profileImageLink
        // if there is a file to be uploaded, dispatch an action that includes a file upload; 
        // otherwise, merely dispatch an action to save the profileImageLink only
        if (saveImageLinkRequest.fileUploadRequest !== undefined) {
          dispatch(imageLinkSaveWithFileUploadRequest(saveImageLinkRequest));
        } else {
          dispatch(imageLinkSaveWithoutFileUploadRequest(saveImageLinkRequest));
        }

        resolve();
      } catch (error: any) {
        reject(error);
      }
    });
  }

  // prepare a variable that may hold a progress message
  let progressMessage: string | undefined = undefined;

  // prepare a variable that may hold alert info
  let alertInfo: IAlertInfo | null = null;

  // prepare a variable to indicate whether a save is in progress
  let saveInProgress: boolean = false;

  if (saveImageWorkflowState !== null && saveImageWorkflowState !== undefined) {

    if (saveImageWorkflowState.workflowState === enumWorkflowState.Success) {
      if (!successfullySavedProfileImage) {
        // set flag to indicate that the profile image has been successfully saved
        setSuccessfullySavedProfileImage(true);
      }
    } else if (saveImageWorkflowState.workflowState === enumWorkflowState.InProgress || saveImageWorkflowState.workflowState === enumWorkflowState.Requested) {
      // get message from the workflow state to display as a progress message
      progressMessage = saveImageWorkflowState.workflowStateMessage ?? undefined;

      // indicate that a save is in progress
      saveInProgress = true;
    } else if (saveImageWorkflowState.workflowState === enumWorkflowState.Failure) {
      let failureMessage = 'Unknown error';
      if (saveImageWorkflowState.workflowError && saveImageWorkflowState.workflowError instanceof Error) {
        failureMessage = saveImageWorkflowState.workflowError.message;
      }
      // create an AlertInfo object and dispatch a request to set the AlertInfo into Redux state
      alertInfo = new AlertInfo(true, enumAlertType.Error, failureMessage);
      dispatch(alertInfoChange(alertInfo));
    }
  }


  return (
    <>
      <GenericPageContainer
        maxWidth="sm"
        showBackButton={true}
        pageTitle={isNewObject ? PageAndViewTitleStringAssets.pageTitle_ManageProfileImageLink_New : PageAndViewTitleStringAssets.pageTitle_ManageProfileImageLink_Existing}
        onCloseAlert={resetSaveImageLinkStatus}
      >
        {profileImageLink && currentUser &&
          <ProfileImageLinkForm
            userId={currentUser.id}
            imageLink={profileImageLink}
            saveRequestInProgress={saveInProgress}
            progressMessage={progressMessage}
            onSubmit={handleFormSubmit}
          />
        }

      </GenericPageContainer>
    </>
  );

} // const ManageProfileImageLinkPage...

export default ManageProfileImageLinkPage;
